import { useEffect, useMemo } from 'react';
import { Paper, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { RadioField } from '@roc/ui';
import { loanTermLengthsOptions } from '../../../utils/constants';
import { observer } from 'mobx-react';
import { PointsAndFeesAccordion } from './pointsAndFeesAccordion';

import { QuoteTypeStore } from '../../../stores/quoteTypeStore';
import { isFixAndFlip, isGroundUp, isStabilizedBridge, isNotBlank } from '@roc/feature-utils';
import { PercentagePicker } from '../../leverage/components/leverageComponents';
import { InterestRateSlider } from './interestRateSlider';
import { BrokerAccordion } from './brokerAccordion';
import { PricingAdjustmentsAccordion } from './pricingAdjustmentsAccordion';
import { useBaseStore, useUserStore } from '@roc/feature-app-core';
import { UserProfileStore } from '@roc/feature-user-profile';

const useStyles = makeStyles(theme => ({
  paper: {
    border: `1px solid lightgray`,
    borderRadius: '16px !important',
    padding: theme.spacing(4),
  },
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    fontSize: '1rem',
  },
  buyDownZoneLegend: {
    color: 'gray',
    fontSize: '16px',
  },
}));

interface EconomicsCardProps {
  store: QuoteTypeStore;
}

export const EconomicsCard = observer(({ store }: EconomicsCardProps) => {
  const classes = useStyles();
  const { loanEconomicsStore } = store;
  const { globalStore } = useBaseStore();
  const { userStore } = useUserStore();
  const formStore = store.loanEconomicsStore.loanEconomicsFormStore;
  const loanData = store.loanEconomicsStore.quoteTypeStore.getQuoteData();
  const { meetsAllHighFicoCriteria } = store.loanEconomicsStore;
  const isSpecifiedLoanType = isFixAndFlip(loanData?.loanSubtype) || isGroundUp(loanData?.loanSubtype) || isStabilizedBridge(loanData?.loanSubtype);
  const displayBuyDownZoneLegend = loanEconomicsStore.buyDownZoneRate > 0;

  const loanTermOptions = useMemo(
    () =>
      meetsAllHighFicoCriteria
        ? [{ label: '12 Months', value: '12' }]
        : loanTermLengthsOptions.map(({ label, value }) => ({
            label,
            value: value.toString(),
          })),
    [meetsAllHighFicoCriteria]
  );

  useEffect(() => {
    if (loanTermOptions.length !== 1) {
      return;
    }
    const soleValue = parseInt(loanTermOptions[0].value, 10);
    const current = formStore.form.fields.loanTermMonths.value;
    if (current !== soleValue) {
      formStore.onFieldChange('loanTermMonths', soleValue);
      store.loanEconomicsStore.fetchBorrowerPointsOptions();
    }
  }, [loanTermOptions, formStore, store.loanEconomicsStore]);

  return (
    !globalStore?.userFeatures?.isBroker ?
      (<Paper className={classes.paper} elevation={0}>
        {userStore.allowLoanPricing ? (
          <Grid container spacing={2}>
            {(isFixAndFlip(loanData?.loanSubtype) || isGroundUp(loanData?.loanSubtype)) && (
              <Grid item xs={12}>
                <PricingAdjustmentsAccordion store={store} />
              </Grid>
            )}
            <Grid item xs={12} style={{ paddingInline: '24px' }}>
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item>
                  {displayBuyDownZoneLegend && (
                    <Typography className={classes.buyDownZoneLegend}>
                      In the <strong>buy-down zone</strong>, the capital provider collects more origination points in exchange for a lower rate.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12} style={{ paddingInline: '24px' }}>
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item>
                  <PercentagePicker
                    fieldName={'interestRate'}
                    label="Borrower Interest Rate"
                    value={formStore.form.fields.interestRate.value}
                    min={loanEconomicsStore.floorRate}
                    max={14}
                    onChange={value =>
                      formStore.onFieldChange('interestRate', value)
                    }
                  />
                </Grid>
                <Grid item>
                  <InterestRateSlider store={store} />
                </Grid>
                <Grid item>
                  <RadioField
                    label={'Loan Duration'}
                    value={(
                      formStore.form.fields.loanTermMonths.value ?? ''
                    ).toString()}
                    options={loanTermOptions}
                    onChange={value => {
                      formStore.onFieldChange('loanTermMonths', parseInt(value));
                      store.loanEconomicsStore.fetchBorrowerPointsOptions();
                    }}
                    fullWidth
                    row={true}
                    testId={'loanTermMonths'}
                    standaloneLabel={true}
                    error={isNotBlank(formStore.form.fields.loanTermMonths.error)}
                    errorText={formStore.form.fields.loanTermMonths.error}
                  />
                </Grid>
              </Grid>
            </Grid>
            {isSpecifiedLoanType ? (
              <>
                <Grid item xs={12}>
                  <PointsAndFeesAccordion store={store} />
                </Grid>
                {!loanEconomicsStore.isManualQuote && (
                  <Grid item xs={12}>
                    <BrokerAccordion store={store} />
                  </Grid>
                )}
              </>
            ) : (
              <>
                {!loanEconomicsStore.isManualQuote && (
                  <Grid item xs={12}>
                    <BrokerAccordion store={store} />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <PointsAndFeesAccordion store={store} />
                </Grid>
              </>
            )}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {
              !loanEconomicsStore.isManualQuote &&
              <Grid item xs={12}>
                <BrokerAccordion store={store} />
              </Grid>
            }
          </Grid>
        )}
      </Paper>) : <></>
  );
});
